import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { AnabizCreditCardInformation } from '@app/anabiz-payment-input/container/anabiz-payment-input-cont.state';
import { SupportComponent } from '@lib/components/support-class';
import { isSP, isTB, isPC, AswValidators } from '@lib/helpers';
import { LoginStatusType, ValidateOptions, ValidationErrorInfo } from '@lib/interfaces';
import { CommonLibService, GetBasicReservationInformationStoreService } from '@lib/services';
import { requiredYmValidator } from './anabiz-payment-input-card-info.state';
import { PaymentInputCardInfoService } from '../../../payment-input/sub-components/payment-input-common/payment-input-card-info/payment-input-card-info.service';
import { initAnabizCreditCardInformation } from '../../container/anabiz-payment-input-cont.state';
import { LocalDateService } from '@common/services';
@Component({
  selector: 'asw-anabiz-payment-input-card-info',
  templateUrl: './anabiz-payment-input-card-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnabizPaymentInputCardInfoComponent extends SupportComponent {
  // 選択クレジットカードナンバー
  public selectedCreditCardNumber: string = '';
  // 選択クレジットカード有効期限
  public expiryDate: string = '';
  // 選択クレジットカード
  @Input() public selectedCard: string = 'other';
  // UATPカード選択チェックボックス表示可否
  public isDisplayUtapCheckbox: boolean = false;
  // 予約基本情報のクレジットカード登録情報・更新チェック表示可否
  public isDisplayCardUpdateCheckbox: boolean = false;
  // 予約基本情報にクレジットカード情報があるかどうか
  private _existRegisterCreditCard: boolean = false;
  // カードブランドアイコン名前マップ
  public cardBrandFile: string | undefined = '';
  // 有効期限選択可能範囲
  yearRange: number = new Date(this._localDateService.getCurrentDateStr()).getFullYear();
  // クレジットカード情報
  @Input() set creditCardInfo(value: AnabizCreditCardInformation) {
    this._creditCardInfo = value;
    this.creditCardInfoChange.emit(value);
  }
  get creditCardInfo(): AnabizCreditCardInformation {
    return this._creditCardInfo;
  }
  private _creditCardInfo: AnabizCreditCardInformation = initAnabizCreditCardInformation();
  @Output() creditCardInfoChange = new EventEmitter<AnabizCreditCardInformation>();

  // カード情報フォームグループ定義
  public cardInfoFormGroup: FormGroup;

  constructor(
    protected _common: CommonLibService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _getBasicReservationInformationStoreService: GetBasicReservationInformationStoreService,
    private _paymentInputCardInfoService: PaymentInputCardInfoService,
    private _localDateService: LocalDateService
  ) {
    super(_common);
    const today = this._localDateService.getCurrentDateStr();
    this.yearRange = new Date(today).getFullYear();

    // クレジットカード情報フォームグループ
    this.cardInfoFormGroup = new FormGroup({
      // UATP
      uatpCard: new FormControl(false, []),
      // クレジットカード番号
      cardNumber: new FormControl('', [
        AswValidators.required({ params: { key: 0, value: 'label.creditCardNumber' } }),
        AswValidators.numeric({ params: { key: 0, value: 'label.creditCardNumber' } }),
        AswValidators.lengths({
          min: 11,
          errorMsgId: 'E0544',
          params: [
            { key: 0, value: 11, dontTranslate: true },
            { key: 1, value: 16, dontTranslate: true },
          ],
        }),
      ]),
      // 有効期限
      cardExpiryDate: new FormControl('', [
        requiredYmValidator({ params: { key: 0, value: 'label.expirationDate' } }),
        AswValidators.numeric({ params: { key: 0, value: 'label.expirationDate' } }),
        this.checkValidDate({ params: { key: 0, value: 'label.expirationDate' } }),
      ]),
      // CVV
      securityCode: new FormControl('', [
        AswValidators.required({ params: { key: 0, value: 'label.securityCode' } }),
        AswValidators.numeric({ params: { key: 0, value: 'label.securityCode' } }),
        AswValidators.lengths({
          min: 3,
          errorMsgId: 'E0544',
          params: [
            { key: 0, value: 3, dontTranslate: true },
            { key: 1, value: 4, dontTranslate: true },
          ],
        }),
      ]),
      // CVV
      securityCodeDisabled: new FormControl('_ _ _'),
      // カード名義
      ownerName: new FormControl('', [
        AswValidators.required({ params: { key: 0, value: 'label.cardholderName' } }),
        AswValidators.alphaSpace({ params: { key: 0, value: 'label.cardholderName' } }),
      ]),
      // いつものカード
      reservation: new FormControl(),
    });
  }

  /**
   * 画面情報表示処理用
   */
  resetCreditCardFormGroup(): void {
    this.cardInfoFormGroup.setValue({
      uatpCard: this.creditCardInfo?.isUatpCard ?? false, // UATP
      cardNumber: this.creditCardInfo?.cardNumber ?? '', // カード番号
      cardExpiryDate: this._paymentInputCardInfoService.convertToDateYmFormData(this.creditCardInfo?.expiryDate ?? ''), // 有効期限
      securityCode: this.creditCardInfo?.securityCode ?? '', // CVV
      securityCodeDisabled: '',
      ownerName: this.creditCardInfo?.holderName ?? '', // 名義
      reservation: this.creditCardInfo?.isCardUpdate ?? false, // 登録
    });
    this.selectedCreditCardNumber = this.creditCardInfo?.cardNumber ?? '';
    this.cardInfoFormGroup.markAsUntouched();
    this.refresh();
    this._changeDetectorRef.markForCheck();
  }

  /**
   * バリデーションチェック
   */
  isValid() {
    const validity = this.cardInfoFormGroup.valid;
    this.cardInfoFormGroup.markAllAsTouched();
    this._changeDetectorRef.detectChanges();
    return validity;
  }

  reload(): void {}

  /**
   * 初期表示
   */
  init(): void {
    const cardType = this._common.anaBizContextStoreService.anaBizContextData.authInfo?.contractType ?? '';
    switch (cardType) {
      case '03':
        this.cardBrandFile = 'cardlogo_jcb';
        break;
      case '04':
        this.cardBrandFile = 'cardlogo_smbccard';
        break;
      case '05':
        this.cardBrandFile = 'cardlogo_dinersclub';
        break;
      case '06':
        this.cardBrandFile = 'cardlogo_uc';
        break;
      case '07':
        this.cardBrandFile = 'cardlogo_americanexpress';
        break;
      case '08':
        this.cardBrandFile = 'cardlogo_mufgcard';
        break;
      case '09':
        this.cardBrandFile = 'cardlogo_dc';
        break;
      default:
        break;
    }

    if (this._common.aswContextStoreService.aswContextData.loginStatus === LoginStatusType.REAL_LOGIN) {
      this.subscribeService(
        'anaBizPaymentInputCreditCard_basicReservationInformation_cardInfo',
        this._getBasicReservationInformationStoreService.getGetBasicReservationInformation$(),
        (value) => {
          if (value?.data?.corporateCreditCardName1 && value?.data?.corporateCreditCardName2) {
            this._existRegisterCreditCard = true;
          } else {
            this._existRegisterCreditCard = false;
          }
          this.refresh();
        }
      );
    }
  }

  destroy(): void {
    this.deleteSubscription('anaBizPaymentInputCreditCard_basicReservationInformation_cardInfo');
  }

  public refresh() {
    this.isDisplayCardUpdateCheckbox =
      !this._existRegisterCreditCard &&
      this.selectedCard === 'other' &&
      this._common.aswContextStoreService.aswContextData.loginStatus === LoginStatusType.REAL_LOGIN;
    this._changeDetectorRef.markForCheck();
  }

  //画面のサイズを切り替えの設定
  public isSp = isSP();
  public isTb = isTB();
  public isPc = isPC();
  private _isSpPre = isSP();
  private _isTbPre = isTB();
  private _isPcPre = isPC();

  /**
   * カード番号変更イベント
   */
  cardNumberChange() {
    this.creditCardInfo!.cardNumber = this.cardInfoFormGroup.controls['cardNumber'].value;
  }

  /**
   * 有効期限変更イベント
   */
  expiryChange() {
    this.creditCardInfo!.expiryDate = this.cardInfoFormGroup.controls['cardExpiryDate'].value;
  }

  /**
   * セキュリティコード変更イベント
   */
  cvvChange() {
    this.creditCardInfo!.securityCode = this.cardInfoFormGroup.controls['securityCode'].value;
  }

  /**
   * 名義人変更イベント
   */
  holderNameChange() {
    this.creditCardInfo!.holderName = this.cardInfoFormGroup.controls['ownerName'].value;
  }
  /**
   * カード登録番号変更イベント
   */
  registerChange() {
    this.creditCardInfo!.isCardUpdate = this.cardInfoFormGroup.controls['reservation'].value;
  }

  /**
   *  有効期限日が過去日であるか判断
   *
   *  @param option　バリデータオプション
   *　@returns バリデータ
   */
  checkValidDate(option: ValidateOptions): ValidatorFn {
    return (control: AbstractControl): { [key: string]: ValidationErrorInfo } | null => {
      if (control.value && control.value.length === 6) {
        const nowDate = this._localDateService.getCurrentDateStr('YYYYMM');
        if (Number(control.value) < Number(nowDate)) {
          return {
            'validate-required-selectYm': {
              errorMsgId: option?.errorMsgId || 'E1767',
              params: option?.params,
            },
          };
        } else {
          return null;
        }
      } else {
        return {
          'validate-required-selectYm': {
            errorMsgId: option?.errorMsgId || 'E0700',
            params: option?.params,
          },
        };
      }
    };
  }
}
